// Global incoming-call banner (Synex Calling V2, Phase 2).
//
// Presentational only — all state/signaling lives in IncomingCallContext. Rendered once,
// fixed in the upper-right corner ABOVE every page, without replacing the current view or
// redirecting to Chat. Used identically for Audio Calls, Video Calls, and Huddles.

import React from "react";
import { Phone, Video, Users, PhoneOff } from "lucide-react";
import Avatar from "../../modules/connect/components/common/Avatar";

const VARIANT = {
  audio: { label: "Incoming Audio Call", Icon: Phone, accent: "text-emerald-600" },
  video: { label: "Incoming Video Call", Icon: Video, accent: "text-blue-600" },
  huddle: { label: "Incoming Huddle", Icon: Users, accent: "text-indigo-600" },
};

export default function IncomingCallBanner({ call, onAnswer, onDecline }) {
  if (!call) return null;

  const variantKey = call.kind === "huddle" ? "huddle" : call.call_type === "video" ? "video" : "audio";
  const { label, Icon, accent } = VARIANT[variantKey];
  const name = call.name || "Someone";

  return (
    <div className="fixed top-4 right-4 z-[2000] w-[330px] animate-in slide-in-from-top-2 fade-in duration-200 print:hidden">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 overflow-hidden">
        <div className="flex items-center gap-3 p-4">
          {/* Caller avatar with a pulsing ring while ringing */}
          <div className="relative shrink-0">
            <span className="absolute inset-0 rounded-full bg-slate-300/50 animate-ping" />
            <Avatar name={name} size={46} className="relative" />
          </div>

          {/* Caller name + call type */}
          <div className="min-w-0 flex-1">
            <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${accent}`}>
              <Icon className="h-3.5 w-3.5" />
              <span className="truncate">{label}</span>
            </div>
            <div className="truncate text-sm font-semibold text-slate-800" title={name}>
              {name}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex border-t border-slate-100">
          <button
            onClick={onDecline}
            className="flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-bold text-rose-600 hover:bg-rose-50 transition-colors"
          >
            <PhoneOff className="h-4 w-4" />
            Decline
          </button>
          <div className="w-px bg-slate-100" />
          <button
            onClick={onAnswer}
            className="flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-bold text-emerald-600 hover:bg-emerald-50 transition-colors"
          >
            <Phone className="h-4 w-4" />
            Answer
          </button>
        </div>
      </div>
    </div>
  );
}
